/*
 * ProductSpecs
 *
 * Product detail content — highlights, technical specifications, and box
 * contents. Keyed by the handles in files/Catalog.js.
 *
 * Sourcing matters here, because these values are stated as fact on a real
 * company's website:
 *
 *   water-sensor, comm-link, bed-shaker
 *     from the owner's manuals (models CFWS10 / CFCL10 / CFBS520), by way of
 *     the storefront, which cites the same manuals.
 *
 *   smoke-sensor, heat-sensor, co-sensor
 *     from the owner's manuals (models OSS20 / OSS20AC, OSH20, OSCO20).
 *
 * Note for anyone updating the network page: the feature list in
 * managers/Home.js states the heat sensor's fixed temperature settings as
 * "117° 135° 175°". The OSH20 manual says 120°F / 135°F / 174°F, with 135°F
 * as the factory default. The manual wins here; that older copy is wrong and
 * should be corrected wherever else it appears.
 */

const ProductSpecs = {

    'water-sensor': {
        highlights: [{
            copy: '23-karat gold contacts sense the first drop of a leak — under sinks, behind the fridge, by the water heater.',
            icon: 'droplet-fill',
            title: 'Reacts the instant it gets wet'
        },{
            copy: 'A built-in low-temperature alert warns you before a pipe has the chance to burst.',
            icon: 'snow2',
            title: 'Catches the freeze, too'
        },{
            copy: 'Every sensor repeats the network’s signal, so coverage grows as more are added.',
            icon: 'broadcast-pin',
            title: 'Extends its own range'
        }],
        inTheBox: ['Water Sensor', '2-sided adhesive feet', 'Rubber feet', 'Vertical mounting screw', 'Instruction manual'],
        specs: [
            { label: 'Detection', value: 'Water contact + low-temperature (freeze)' },
            { label: 'Sensor contacts', value: '23-karat gold-plated' },
            { label: 'On-device alerts', value: 'Internal horn + backlit LED icons' },
            { label: 'Remote alerts', value: 'Text, email & app' },
            { label: 'Wireless', value: 'Proprietary radio · 905.2 / 913.2 MHz' },
            { label: 'Range', value: 'Up to 200 ft (70 m), open area' },
            { label: 'Network', value: 'Repeats signals to extend coverage' },
            { label: 'Power', value: 'Internal 10 year lithium battery' },
            { label: 'Mounting', value: 'Adhesive feet, rubber feet, or vertical screw' },
            { label: 'Works with', value: 'Comm Link + mobile app' },
            { label: 'Assembly', value: 'Assembled in the U.S.A.' }
        ],
        tagline: 'Early leak & freeze detection'
    },

    'comm-link': {
        highlights: [{
            copy: 'Connects every device on the network and relays its status to your phone in real time.',
            icon: 'broadcast-pin',
            title: 'The brain of the network'
        },{
            copy: 'Plug into Ethernet for the most reliable link, or place it anywhere over Wi-Fi.',
            icon: 'ethernet',
            title: 'Wired or wireless'
        },{
            copy: 'AC powered with no batteries to replace, for peace of mind.',
            icon: 'plug-fill',
            title: 'Always on'
        }],
        inTheBox: ['Comm Link', 'AC power adapter', '3 ft Ethernet cable', 'Instruction manual'],
        specs: [
            { label: 'Internet connectivity', value: 'Ethernet (wired) + Wi-Fi 2.4 GHz' },
            { label: 'Role', value: 'Relays all sensor data to the internet for phone alerts' },
            { label: 'Sensor network', value: 'Proprietary radio · 905 / 913 MHz' },
            { label: 'On-device', value: '4 backlit status icons + internal piezo horn' },
            { label: 'Controls', value: 'Dual-function test / silence button' },
            { label: 'Power', value: 'AC adapter — no batteries required' },
            { label: 'Works with', value: 'Every device on the network + mobile app' },
            { label: 'Assembly', value: 'Assembled in the U.S.A.' }
        ],
        tagline: 'The hub that connects it all'
    },

    'bed-shaker': {
        highlights: [{
            copy: 'The shaker slips under the mattress and vibrates the bed the moment any alarm sounds.',
            icon: 'moon-stars-fill',
            title: 'Wakes you through the mattress'
        },{
            copy: 'Tuned at ear level for the hearing impaired, heavy sleepers, and children.',
            icon: 'volume-up-fill',
            title: 'A 520 Hz low-frequency horn'
        },{
            copy: 'Any device on the network can trigger it — smoke, heat, carbon monoxide or water.',
            icon: 'link-45deg',
            title: 'Connects to every alarm'
        }],
        inTheBox: ['Speaker unit', 'Under-mattress shaker unit', '3 × Duracell DL123 batteries', 'Instruction manual'],
        specs: [
            { label: 'Alert type', value: '520 Hz low-frequency horn + vibration' },
            { label: 'Shaker unit', value: 'Inserts under the mattress; vibrates in alarm' },
            { label: 'Cord length', value: '6 ft (bed to nightstand)' },
            { label: 'On-device', value: '4 illuminated status icons' },
            { label: 'Controls', value: 'Dual-function test / silence button' },
            { label: 'Power', value: '3 × Lithium Manganese (Duracell DL123)' },
            { label: 'Placement', value: 'Bedrooms / sleeping rooms' },
            { label: 'Works with', value: 'Comm Link + mobile app' },
            { label: 'Assembly', value: 'Assembled in the U.S.A.' }
        ],
        tagline: 'Never sleep through an alarm'
    },

    'smoke-sensor': {
        placement: {
            avoid: [
                { note: 'Within 5 ft of cooking appliances — searing a steak would set it off.', where: 'Kitchens' },
                { note: 'Within 3 ft of the door. Steam from a shower reads as smoke.', where: 'Bathrooms' },
                { note: 'Vehicle exhaust causes nuisance alarms. Use a heat sensor here.', where: 'Garages' },
                { note: 'Unconditioned space, outside the 40–100°F range the sensor is rated for.', where: 'Attics & crawlspaces' },
                { note: 'Dust and combustion by-products trigger it. Use a heat sensor here.', where: 'Laundry & furnace rooms' }
            ],
            where: [
                { note: 'NFPA 72 requires one inside every sleeping room.', required: true, where: 'Every bedroom' },
                { note: 'Outside each sleeping area, within 21 ft of any bedroom door.', required: true, where: 'Hallways' },
                { note: 'Required on every level, including the basement — near the bottom of the stairs.', required: true, where: 'Every level' },
                { note: 'Where a fire is most likely to start and grow before anyone sees it.', where: 'Living areas' }
            ]
        },
        highlights: [{
            copy: 'Two detection technologies catch slow smouldering fires and fast flaming ones, with fewer false alarms than a single-sensor alarm.',
            icon: 'fire',
            title: 'Catches both kinds of fire'
        },{
            copy: 'The optical chamber lifts out and can be cleaned, so dust build-up does not quietly retire the alarm.',
            icon: 'wind',
            title: 'A chamber you can clean'
        },{
            copy: 'Infrared optics scan the chamber every four seconds, and the sensor reports its own health.',
            icon: 'activity',
            title: 'Checks itself, constantly'
        }],
        inTheBox: ['Smoke Sensor', 'Mounting bracket', '2 × 1" mounting screws', 'Owner’s manual'],
        specs: [
            { label: 'Detection', value: 'Photoelectric smoke + supplemental fixed-temperature heat' },
            { label: 'Sensitivity', value: '1.78 %/ft ± 0.45 % OBS' },
            { label: 'Supplemental heat', value: 'Alarms at 135°F (57°C)' },
            { label: 'Smoke chamber', value: '360° unobstructed; removable and cleanable' },
            { label: 'On-device alerts', value: 'Piezo horn + 4 backlit LED icons' },
            { label: 'Alarm patterns', value: 'Fire — 3 beeps · CO from another sensor — 4 beeps' },
            { label: 'Remote alerts', value: 'Text, email & app (with the Comm Link)' },
            { label: 'Wireless', value: 'Proprietary radio · 905.2 / 913.2 MHz' },
            { label: 'Range', value: 'Up to 200 ft (70 m), open area' },
            { label: 'Network', value: 'Repeats signals · up to 18 devices, max 12 smoke sensors' },
            { label: 'Silence', value: '5 minutes' },
            { label: 'Power', value: 'OSS20 — 3 VDC · OSS20AC — 120 VAC 60 Hz' },
            { label: 'Battery', value: 'Sealed non-replaceable lithium manganese' },
            { label: 'Operating temperature', value: '40°F – 100°F (4°C – 38°C)' },
            { label: 'Operating humidity', value: '0 – 95 % non-condensing' },
            { label: 'Dimensions', value: '5.1" × 5.1" × 1.7" (13 × 13 × 4.3 cm)' },
            { label: 'Weight', value: '0.8 lb with bracket' },
            { label: 'Listings', value: 'cETLus · CSFM · UL 217 9th Ed. · ULC S531' },
            { label: 'Assembly', value: 'Assembled in the U.S.A.' }
        ],
        tagline: 'Two technologies, fewer false alarms'
    },

    'heat-sensor': {
        placement: {
            avoid: [
                { note: 'A sleeping room needs a smoke sensor — that is a code requirement, not a preference.', where: 'Bedrooms' },
                { note: 'Outside sleeping areas a smoke sensor is required instead.', where: 'Hallways' }
            ],
            where: [
                { note: 'Where a smoke alarm would go off every time you cook.', setting: '120°F', where: 'Kitchen' },
                { note: 'Dryer lint is highly flammable and collects where nobody looks.', setting: '120°F', where: 'Laundry room' },
                { note: 'Exhaust gases will not trigger a heat sensor the way they trigger smoke.', setting: '135°F', where: 'Garage' },
                { note: 'A CO sensor has to sit 20 ft from combustion sources, so heat covers this room.', setting: '135°F', where: 'Utility & furnace room' },
                { note: 'Dusty enough that a smoke alarm would nuisance-alarm.', setting: '135°F', where: 'Basement & crawlspace' },
                { note: 'Runs hot in summer, so the threshold goes high. 135°F in cooler climates.', setting: '174°F', where: 'Attic' }
            ]
        },
        highlights: [{
            copy: 'Attics, garages and laundry rooms set off smoke alarms for reasons that are not fires. A heat sensor watches those rooms instead.',
            icon: 'building',
            title: 'For the rooms smoke alarms cannot watch'
        },{
            copy: 'Three fixed-temperature settings — 120°, 135° and 174° — so the sensor suits the room it is in. A kitchen and an attic are not the same problem.',
            icon: 'sliders',
            title: 'Set to the room'
        },{
            copy: 'Rate-of-rise detection catches a fast temperature climb before the fixed threshold is ever reached.',
            icon: 'graph-up-arrow',
            title: 'Watches the rate, not just the number'
        }],
        inTheBox: ['Heat Sensor', 'Mounting bracket', '2 × 1" mounting screws', 'Self-adhesive warning label', 'Owner’s manual'],
        specs: [
            { label: 'Detection', value: 'Fixed temperature + rate-of-rise' },
            { label: 'Temperature settings', value: '120°F (49°C) · 135°F (57°C) · 174°F (79°C) — 135°F default' },
            { label: 'Rate of rise', value: '20°F (11°C) per minute, above 100°F (38°C)' },
            { label: 'Space rating', value: '50 ft (15 m) — covers a 35 × 35 ft room' },
            { label: 'On-device alerts', value: 'Piezo horn + 4 backlit LED icons' },
            { label: 'Remote alerts', value: 'Text, email & app (with the Comm Link)' },
            { label: 'Wireless', value: 'Proprietary radio · 905.2 / 913.2 MHz' },
            { label: 'Range', value: 'Up to 200 ft (70 m), open area' },
            { label: 'Silence', value: '5 minutes' },
            { label: 'Power', value: '3 VDC' },
            { label: 'Battery', value: 'Sealed non-replaceable lithium manganese' },
            { label: 'Operating temperature', value: '−20°F to 135°F (−29°C to 57°C)' },
            { label: 'Operating humidity', value: '10 – 95 % non-condensing' },
            { label: 'Dimensions', value: '5.1" × 5.1" × 1.6" (13 × 13 × 4.1 cm)' },
            { label: 'Weight', value: '0.6 lb (0.27 kg) with bracket' },
            { label: 'Listings', value: 'cETLus · CSFM · UL 539 8th Ed.' },
            { label: 'Assembly', value: 'Assembled in the U.S.A.' }
        ],
        tagline: 'Fire cover for the rooms smoke alarms miss'
    },

    'co-sensor': {
        placement: {
            avoid: [
                { note: 'Within 5 ft of cooking appliances.', where: 'Kitchens' },
                { note: 'Vehicle exhaust causes nuisance alarms.', where: 'Garages' },
                { note: 'Unconditioned space, outside the sensor’s rated range.', where: 'Attics & crawlspaces' },
                { note: 'A CO sensor must sit at least 20 ft from any combustion source.', where: 'Furnace & laundry rooms' }
            ],
            where: [
                { note: 'Required on every level, within 21 ft of any bedroom door.', required: true, where: 'Hallways' },
                { note: 'Required on every level of the home, including the basement.', required: true, where: 'Every level' },
                { note: 'Added protection where people are asleep and least able to react.', where: 'Bedrooms' },
                { note: 'Where the family spends the evening.', where: 'Living areas' }
            ]
        },
        highlights: [{
            copy: 'Carbon monoxide is odorless, colorless and tasteless. There is no version of this you can detect yourself.',
            icon: 'cloud-haze2-fill',
            title: 'The one you cannot smell'
        },{
            copy: 'An electro-chemical sensor under Texas Instruments microprocessor control watches for levels long before they turn lethal.',
            icon: 'cpu-fill',
            title: 'Warns before levels turn lethal'
        },{
            copy: 'On-device horn and light, plus text, email and app — the alert reaches you whether or not you are home.',
            icon: 'bell-fill',
            title: 'Alerts several ways at once'
        }],
        inTheBox: ['Carbon Monoxide Sensor', 'Mounting bracket', '2 × 1" mounting screws', '2 × self-adhesive warning labels', 'Owner’s manual'],
        specs: [
            { label: 'Detection', value: 'Electrochemical carbon monoxide + supplemental fixed-temperature heat' },
            { label: 'Response time', value: '400 PPM — 4 to 15 min · 150 PPM — 10 to 50 min · 70 PPM — 60 to 240 min' },
            { label: 'Below 30 PPM', value: 'Will not activate' },
            { label: 'Supplemental heat', value: 'Alarms at 135°F (57°C)' },
            { label: 'On-device alerts', value: 'Piezo horn + 4 backlit LED icons' },
            { label: 'Alarm patterns', value: 'CO — 4 beeps · Fire from another sensor — 3 beeps' },
            { label: 'Remote alerts', value: 'Text, email & app (with the Comm Link)' },
            { label: 'Wireless', value: 'Proprietary radio · 905.2 / 913.2 MHz' },
            { label: 'Range', value: 'Up to 200 ft (70 m), open area' },
            { label: 'Silence', value: '5 minutes' },
            { label: 'Power', value: '3 VDC' },
            { label: 'Battery', value: 'Sealed non-replaceable lithium manganese' },
            { label: 'Operating temperature', value: '40°F – 100°F (4°C – 38°C)' },
            { label: 'Operating humidity', value: '10 – 95 % non-condensing' },
            { label: 'Dimensions', value: '5.1" × 5.1" × 1.6" (13 × 13 × 4.1 cm)' },
            { label: 'Weight', value: '0.6 lb (0.27 kg) with bracket' },
            { label: 'Listings', value: 'cETLus · CSFM · UL 2034 · CSA 6.19-17' },
            { label: 'Assembly', value: 'Assembled in the U.S.A.' }
        ],
        tagline: 'Monitoring for the silent killer'
    }
};

export const getSpecs = handle => ProductSpecs[handle] || null;

export default ProductSpecs;
